// controllers/useCertificateController.js
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useSession } from '../context/SessionContext';

export default function useCertificateController() {
  const { session } = useSession();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch certificates from the database
  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const { data: certificates, error: certificatesError } = await supabase
        .from('certificate')
        .select('*')
        .eq('user_id', session.user.id);

      if (certificatesError) throw certificatesError;

      setCertificates(certificates);
    } catch (error) {
      console.error('Error fetching certificates:', error);
      setError('Failed to fetch certificates');
    } finally {
      setLoading(false);
    }
  };

  // Add a new certificate
  const addCertificate = async (certificateData) => {
    try {
      setLoading(true);
      const { error: certificateError } = await supabase
        .from('certificate')
        .insert([{ 
          ...certificateData, 
          user_id: session.user.id,
          cred_id: certificateData.cred_id ? Number(certificateData.cred_id) : null // Convert to number or set to null if not provided
        }])
        .select()
        .single();

      if (certificateError) throw certificateError;

      // Refresh certificates list
      await fetchCertificates();
    } catch (error) {
      console.error('Error adding certificate:', error);
      setError('Failed to add certificate');
    } finally {
      setLoading(false);
    }
  };

  // Update an existing certificate
  const updateCertificate = async (id, certificateData) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('certificate')
        .update({ 
          ...certificateData, 
          cred_id: certificateData.cred_id ? Number(certificateData.cred_id) : null // Convert to number or set to null if not provided
        })
        .eq('id', id)
        .single();

      if (error) throw error;

      // Refresh certificates list
      await fetchCertificates();
    } catch (error) {
      console.error('Error updating certificate:', error);
      setError('Failed to update certificate');
    } finally {
      setLoading(false);
    }
  };

  // Delete a certificate
  const deleteCertificate = async (id) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('certificate')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Refresh certificates list
      await fetchCertificates();
    } catch (error) {
      console.error('Error deleting certificate:', error);
      setError('Failed to delete certificate');
    } finally {
      setLoading(false);
    }
  };

  // Fetch certificates on component mount
  useEffect(() => {
    if(session){
      setLoading(false);
      fetchCertificates();
    }
  }, [session]);

  return {
    certificates,
    loading,
    error,
    addCertificate,
    updateCertificate,
    deleteCertificate,
  };
}